/**
 * Marco muestral.
 *
 * Además de mostrar el marco, VERIFICA que lo cargado en la base siga
 * reproduciendo la aritmética publicada en §4 y §5. Si alguien edita una lista
 * nominal y rompe un total del documento, aparece aquí de inmediato en lugar de
 * descubrirse meses después al ponderar.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import {
  BarChart,
  StatusChip,
  TableView,
  Tile,
  fmtDecimal,
  fmtInt,
  fmtPct,
} from '../components/charts';

export function FramePage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [stratumFilter, setStratumFilter] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);

  const overview = useQuery({ queryKey: ['frame', 'overview'], queryFn: api.frameOverview });
  const sections = useQuery({
    queryKey: ['frame', 'sections', stratumFilter, selectedOnly],
    queryFn: () =>
      api.sections({
        ...(stratumFilter ? { stratumId: stratumFilter } : {}),
        ...(selectedOnly ? { selectedOnly: 'true' } : {}),
      }),
  });
  const clusters = useQuery({
    queryKey: ['frame', 'clusters', stratumFilter],
    queryFn: () => api.clusters(stratumFilter ? { stratumId: stratumFilter } : {}),
  });
  const targets = useQuery({ queryKey: ['frame', 'targets'], queryFn: api.frameTargets });

  if (overview.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  const data = overview.data;
  if (!data) return <div className="callout callout--critical">No se pudo cargar el marco.</div>;

  const failedChecks = data.consistency.filter((c) => !c.ok);

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Marco muestral</h1>
        <p className="page-sub">
          Reconstruido con el cómputo por casilla de la elección de Ayuntamiento de Tecate 2024
          (IEEBC): {fmtInt(data.totals.sections)} secciones y{' '}
          {fmtInt(data.totals.listaNominal2024)} electores, escalado al total INE de enero de 2026.
        </p>
      </div>

      {/* --------------------- Verificación de consistencia ------------------ */}

      {failedChecks.length > 0 ? (
        <div className="callout callout--critical">
          <span className="callout__title">
            <StatusChip status="critical">
              {failedChecks.length} inconsistencia(s) con el documento
            </StatusChip>
          </span>
          {failedChecks.map((check) => (
            <p key={check.check} style={{ margin: '4px 0' }}>
              <span className="ref">{check.reference}</span> {check.check}: la base tiene{' '}
              {fmtInt(check.actual)} y el documento publica {fmtInt(check.expected)}.
            </p>
          ))}
        </div>
      ) : (
        <div className="callout callout--good">
          <span className="callout__title">
            <StatusChip status="good">El marco reproduce la aritmética del documento</StatusChip>
          </span>
          Las {data.consistency.length} verificaciones de §4 y §5 coinciden con los totales
          publicados.
        </div>
      )}

      {/* ----------------------------- Totales ------------------------------ */}

      <div className="grid grid--4">
        <Tile label="Secciones del marco" reference="§4.1" value={fmtInt(data.totals.sections)} />
        <Tile
          label="Secciones seleccionadas"
          reference="§5.1"
          value={fmtInt(data.totals.selectedSections)}
          meta={`Cubren ${fmtPct(data.totals.padronCoverage, 0)} del padrón`}
        />
        <Tile
          label="Conglomerados"
          reference="§5.2"
          value={fmtInt(data.totals.clusters)}
          meta={`${fmtInt(data.totals.targetInterviews)} entrevistas`}
        />
        <Tile
          label="Lista nominal 2024"
          reference="§4.1"
          value={fmtInt(data.totals.listaNominal2024)}
        />
      </div>

      {/* ---------------------------- Estratos ------------------------------ */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Estratos <span className="ref">§4.2</span>
            </h2>
            <p className="panel__note">
              Zona × participación histórica. Urbanas: A ≥ 52%, B 48–52%, C &lt; 48%. Rurales
              (0724–0734) en D y de creación reciente (2044–2047) en E.
            </p>
          </div>
        </div>

        <BarChart
          rows={data.strata.map((stratum) => ({
            key: stratum.id,
            label: `${stratum.id} · ${stratum.name}`,
            value: stratum.listaNominal2024,
            valueLabel: fmtPct(stratum.padronShare, 1),
            tooltip: `${stratum.name}: ${fmtInt(stratum.listaNominal2024)} electores (${fmtPct(stratum.padronShare, 1)} del padrón) · meta ${fmtInt(stratum.targetN)} entrevistas · participación 2024 ${fmtPct(stratum.participation2024, 1)}`,
          }))}
        />

        <div className="table-wrap" style={{ marginTop: 16 }}>
          <table className="table table--numeric">
            <thead>
              <tr>
                <th>Estrato</th>
                <th>Secs.</th>
                <th>Sel.</th>
                <th>Lista nominal</th>
                <th>% padrón</th>
                <th>Part. 2024</th>
                <th>Coal. MORENA</th>
                <th>PES BC</th>
                <th>PAN</th>
                <th>Meta n</th>
                <th>Congl.</th>
                <th>Margen</th>
              </tr>
            </thead>
            <tbody>
              {data.strata.map((stratum) => (
                <tr key={stratum.id}>
                  <td>
                    <div className="strong">
                      {stratum.id} · {stratum.name}
                    </div>
                    {stratum.readAsTrendOnly ? (
                      <StatusChip status="warning">Sólo tendencia</StatusChip>
                    ) : null}
                  </td>
                  <td>{fmtInt(stratum.sectionCount)}</td>
                  <td>{fmtInt(stratum.selectedSectionCount)}</td>
                  <td>{fmtInt(stratum.listaNominal2024)}</td>
                  <td>{fmtPct(stratum.padronShare, 1)}</td>
                  <td>{fmtPct(stratum.participation2024, 1)}</td>
                  <td>{fmtPct(stratum.politicalProfile.coalicionMorena2024, 1)}</td>
                  <td>{fmtPct(stratum.politicalProfile.pesBc2024, 1)}</td>
                  <td>{fmtPct(stratum.politicalProfile.pan2024, 1)}</td>
                  <td className="strong">{fmtInt(stratum.targetN)}</td>
                  <td>{fmtInt(stratum.clusterCount)}</td>
                  <td>±{fmtDecimal(stratum.marginOfError.theoretical * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <details className="note">
          <summary>Qué discrimina la estratificación</summary>
          <p>
            Los perfiles políticos confirman que los estratos separan poblaciones distintas: el voto
            PES BC es de 22–24% en los estratos urbanos y en E, pero de sólo 11.5% en el rural,
            donde la coalición MORENA alcanza 60%. El PAN es más fuerte en A (10.6%).
          </p>
        </details>
      </div>

      {/* ---------------------- Metas de monitoreo -------------------------- */}

      {targets.data ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Metas de monitoreo <span className="ref">§3</span>
              </h2>
              <p className="panel__note">
                <span className="strong">No son cuotas.</span> {targets.data.usageNote}
              </p>
            </div>
          </div>

          <div className="grid grid--2">
            <div>
              <p className="field__label">Género (proporcional al padrón 2026)</p>
              <TableView
                columns={['Categoría', '% padrón', 'Meta n = 800']}
                rows={targets.data.gender.map((t) => [t.label, fmtPct(t.padronShare, 1), fmtInt(t.target)])}
              />
              <BarChart
                rows={targets.data.gender.map((t) => ({
                  key: t.code,
                  label: t.label,
                  value: t.target,
                  tooltip: `${t.label}: meta ${fmtInt(t.target)} (${fmtPct(t.padronShare, 1)} del padrón)`,
                }))}
              />
            </div>
            <div>
              <p className="field__label">Grupos operativos de edad</p>
              <BarChart
                rows={targets.data.ageGroups.map((t) => ({
                  key: t.code,
                  label: t.label,
                  value: t.target,
                  tooltip: `${t.label}: meta ${fmtInt(t.target)} (${fmtPct(t.padronShare, 1)} del padrón)`,
                }))}
              />
              <p className="faint" style={{ marginTop: 10 }}>
                El grupo 18–29 es el de mayor abstencionismo histórico en Baja California. La muestra
                representa la opinión ciudadana; la del votante probable se obtiene en el análisis.
              </p>
            </div>
          </div>

          <div className="divider" />

          <p className="field__label">
            Escolaridad <span className="ref">§3.3</span>
          </p>
          <p className="muted">{targets.data.educationNote}</p>
          <TableView
            columns={['Nivel máximo', '% aprox. Tecate', 'Referencia n = 800']}
            rows={targets.data.education.map((t) => [
              t.label,
              fmtPct(t.padronShare, 0),
              fmtInt(t.target),
            ])}
          />
        </div>
      ) : null}

      {/* ----------------------------- Filtros ------------------------------ */}

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="stratum">
            Estrato
          </label>
          <select
            id="stratum"
            className="select"
            value={stratumFilter}
            onChange={(e) => setStratumFilter(e.target.value)}
          >
            <option value="">Todos</option>
            {data.strata.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id} · {s.name}
              </option>
            ))}
          </select>
        </div>
        <label className="row" style={{ fontSize: 14, paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={selectedOnly}
            onChange={(e) => setSelectedOnly(e.target.checked)}
          />
          Sólo secciones con conglomerado
        </label>
      </div>

      {/* ---------------------------- Secciones ----------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Secciones <span className="ref">§4.3</span>
            </h2>
            <p className="panel__note">
              Los nombres de colonia provienen del encarte 2016 y son referencia preliminar: las
              secciones 1970–2047 no tienen colonia confirmada. Validar con la cartografía INE y el
              encarte IEEBC 2024 antes de capacitar.
            </p>
          </div>
        </div>

        {sections.data ? (
          <div className="table-wrap scroll-y">
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>Sec.</th>
                  <th>Colonia / localidad</th>
                  <th>Estrato</th>
                  <th>Casillas</th>
                  <th>LN 2024</th>
                  <th>LN 2026 est.</th>
                  <th>% padrón</th>
                  <th>Part.</th>
                  <th>Congl.</th>
                  <th>Entrev.</th>
                  <th>Geometría</th>
                </tr>
              </thead>
              <tbody>
                {sections.data.map((section) => (
                  <tr key={section.seccion}>
                    <td className="strong">{section.seccion}</td>
                    <td style={{ textAlign: 'left' }}>
                      {section.colonia}
                      {!section.coloniaConfirmed ? (
                        <span className="faint"> (preliminar)</span>
                      ) : null}
                      {section.cartographySecciones.length > 1 ||
                      section.cartographySecciones[0] !== section.seccion ? (
                        <div className="faint mono">
                          cartografía: {section.cartographySecciones.join(', ')}
                        </div>
                      ) : null}
                    </td>
                    <td>{section.stratum.id}</td>
                    <td>
                      {section.casillas}
                      {section.casillasExtraordinarias > 0
                        ? ` (${section.casillasExtraordinarias} E)`
                        : ''}
                    </td>
                    <td>{fmtInt(section.listaNominal2024)}</td>
                    <td className="faint">
                      {section.listaNominal2026Estimated
                        ? fmtInt(section.listaNominal2026Estimated)
                        : '—'}
                    </td>
                    <td>{fmtPct(section.padronShare, 1)}</td>
                    <td>{fmtPct(section.participation2024, 1)}</td>
                    <td>{section.clusterCount || '—'}</td>
                    <td className="strong">{section.targetInterviews || '—'}</td>
                    <td>
                      {section.hasGeometry ? (
                        <StatusChip status="good">Sí</StatusChip>
                      ) : section.cartographySecciones[0] !== section.seccion ? (
                        <StatusChip status="good">Equivalencia</StatusChip>
                      ) : (
                        <StatusChip status="critical">Falta</StatusChip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spinner" />
        )}
      </div>

      {/* --------------------------- Conglomerados -------------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Conglomerados <span className="ref">§5.2</span>
            </h2>
            <p className="panel__note">
              Toma casi fija de 17–21 entrevistas. Con PPS y toma fija, cada elector de un estrato
              tiene la misma probabilidad de selección: la muestra es autoponderada dentro de cada
              estrato.
            </p>
          </div>
        </div>

        {clusters.data ? (
          <div className="table-wrap scroll-y">
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>Congl.</th>
                  <th>Sección / área</th>
                  <th>Referencia</th>
                  <th>Meta</th>
                  <th>Viviendas est.</th>
                  <th>Salto k</th>
                  <th>Manzanas</th>
                  <th>Asignado a</th>
                  <th>Levantadas</th>
                  {can('frame:write') ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {clusters.data.map((cluster) => (
                  <ClusterRow
                    key={cluster.id}
                    cluster={cluster}
                    canEdit={can('frame:write')}
                    onSaved={() => void queryClient.invalidateQueries({ queryKey: ['frame'] })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spinner" />
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */

function ClusterRow({
  cluster,
  canEdit,
  onSaved,
}: {
  cluster: {
    id: string;
    seccion: number;
    areaCode: string;
    areaLabel: string;
    reference: string;
    targetInterviews: number;
    estimatedDwellings: number | null;
    systematicSkipK: number | null;
    manzanaSelections: { id: string; cvegeo: string | null }[];
    manzanasToSelect: number;
    assignments: { encuestador: { code: string; name: string } }[];
    interviewCount: number;
  };
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [dwellings, setDwellings] = useState(String(cluster.estimatedDwellings ?? ''));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.setDwellings(cluster.id, Number.parseInt(dwellings, 10)),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onSaved();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo guardar.'),
  });

  return (
    <tr>
      <td className="strong">{cluster.id}</td>
      <td>
        {cluster.seccion}
        <div className="faint">{cluster.areaLabel}</div>
      </td>
      <td style={{ textAlign: 'left', maxWidth: 260 }} className="faint">
        {cluster.reference}
      </td>
      <td className="strong">{cluster.targetInterviews}</td>
      <td>
        {editing ? (
          <>
            <input
              className="input"
              style={{ width: 96 }}
              value={dwellings}
              onChange={(e) => setDwellings(e.target.value)}
              inputMode="numeric"
            />
            {error ? <div className="field__error">{error}</div> : null}
          </>
        ) : (
          (cluster.estimatedDwellings ?? '—')
        )}
      </td>
      <td>
        {cluster.systematicSkipK !== null ? (
          <span className="strong">1 / {cluster.systematicSkipK}</span>
        ) : (
          <StatusChip status="warning">Sin definir</StatusChip>
        )}
      </td>
      <td>
        {cluster.manzanaSelections.length > 0 ? (
          <span>
            {cluster.manzanaSelections.length} / {cluster.manzanasToSelect}
          </span>
        ) : (
          <StatusChip status="warning">Sin sortear</StatusChip>
        )}
      </td>
      <td className="faint" style={{ textAlign: 'left' }}>
        {cluster.assignments.length > 0
          ? cluster.assignments.map((a) => a.encuestador.code).join(', ')
          : '—'}
      </td>
      <td>{cluster.interviewCount}</td>
      {canEdit ? (
        <td>
          {editing ? (
            <div className="row">
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => save.mutate()}
                disabled={save.isPending || !dwellings}
              >
                Guardar
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setEditing(false);
                  setDwellings(String(cluster.estimatedDwellings ?? ''));
                  setError(null);
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setEditing(true)}
            >
              Viviendas
            </button>
          )}
        </td>
      ) : null}
    </tr>
  );
}
